import { z } from 'zod';

/**
 * De schrijfhulp met AI (ADR-0102): een bericht laten schrijven of laten corrigeren.
 *
 * De client stuurt het document van de editor (`bodyJson`) en nooit HTML; de server zet het om
 * met dezelfde witte lijst als bij het bewaren (ADR-0037). Wat terugkomt is HTML met alleen de
 * tags die een bericht mag bevatten. De editor leest die in met zijn eigen schema, dus wat er
 * niet in past, verdwijnt daar — en het bewaren rendert daarna opnieuw uit de JSON.
 *
 * Niets wordt automatisch bewaard of gepubliceerd: de schrijver ziet het voorstel en kiest.
 */

/** De taal waarin het bericht geschreven wordt; die van het opstelscherm. */
export const assistantLanguageSchema = z.enum(['nl', 'fr', 'en']);
export type AssistantLanguage = z.infer<typeof assistantLanguageSchema>;

export const assistantStatusSchema = z.object({
  /** Is er een AI-dienst? Zo niet, dan toont de client de knoppen niet. */
  enabled: z.boolean(),
  /** `claude` of `pseudo` (de schijnhulp zonder sleutel, alleen buiten productie). */
  provider: z.string().nullable(),
});
export type AssistantStatus = z.infer<typeof assistantStatusSchema>;

export const assistantWriteRequestSchema = z.object({
  /** Wat de schrijver wil zeggen, in eigen woorden. */
  instruction: z.string().trim().min(3).max(2000),
  language: assistantLanguageSchema,
  /** Toon van het bericht. */
  tone: z.enum(['neutraal', 'formeel', 'vriendelijk', 'kort']).default('neutraal'),
  /** Wat er al staat, zodat de hulp erop kan verderbouwen. Mag leeg zijn. */
  title: z.string().max(200).default(''),
  bodyJson: z.unknown().optional(),
});
export type AssistantWriteRequest = z.infer<typeof assistantWriteRequestSchema>;

export const assistantCorrectRequestSchema = z.object({
  language: assistantLanguageSchema,
  title: z.string().max(200).default(''),
  bodyJson: z.unknown(),
});
export type AssistantCorrectRequest = z.infer<typeof assistantCorrectRequestSchema>;

export const assistantSuggestionSchema = z.object({
  title: z.string(),
  /** HTML met alleen de tags van een bericht: p, h2-h4, ul, ol, li, strong, em, u, a, br. */
  bodyHtml: z.string(),
  /** Bij corrigeren: een korte uitleg van wat er veranderd is. Bij schrijven leeg. */
  notes: z.array(z.string()),
});
export type AssistantSuggestion = z.infer<typeof assistantSuggestionSchema>;
